import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import EDatePicker from '@/components/everridge/EDatePicker';
import { useCalendarFilter } from '@/features/calendar/hooks/useCalendarFilter';
import { CalendarPageFilterFormSchema } from '@/features/calendar/validators/CalendarPageFilterValidator';
import EEnumPopupPicker from '@/components/everridge/EEnumPopupPicker';
import { CalendarEventType } from '@/features/calendar/types/calendar-dto';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { cn } from '@/lib/utils';

type FormData = z.infer<typeof CalendarPageFilterFormSchema>;

type Props = {
	onClose: () => void;
	isMobile?: boolean;
};

const CalendarPageFilter: FC<Props> = ({ onClose, isMobile }) => {
	const [isOpen, setIsOpen] = useState(true);
	const { t } = useTranslation();

	const { filter, setFilter } = useCalendarFilter();

	const { control, handleSubmit, reset } = useForm<FormData>({
		resolver: zodResolver(CalendarPageFilterFormSchema),
		values: {
			startDate: filter.startDate,
			endDate: filter.endDate,
			types: filter.types,
		},
	});

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const handleSubmitFilterForm = (form: FormData) => {
		setFilter(form);
		handleClose();
	};

	const handleResetFilter = () => {
		reset({
			startDate: null,
			endDate: null,
			types: [],
		});
	};

	const reservationTypes = Object.values(CalendarEventType).filter(
		(value) => typeof value === 'string',
	);

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t('elements.components.filter.title')}
						<DialogClose
							className={cn(
								'ml-auto flex items-center justify-center cursor-pointer',
								isMobile &&
									'size-11 border border-popover-100 rounded-full',
							)}>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
					<DialogDescription />
				</DialogHeader>
				<div className='flex flex-col gap-4'>
					<div className='flex gap-4'>
						<EDatePicker
							control={control}
							fieldName={'startDate'}
							label={t('elements.form.startDate.label')}
							placeholder={t(
								'elements.form.startDate.placeholder',
							)}
						/>
						<EDatePicker
							control={control}
							fieldName={'endDate'}
							label={t('elements.form.endDate.label')}
							placeholder={t('elements.form.endDate.placeholder')}
						/>
					</div>
					<EEnumPopupPicker
						control={control}
						fieldName={'types'}
						label={t('elements.form.reservationType.label')}
						placeholder={t(
							'elements.form.reservationType.placeholder',
						)}
						list={reservationTypes}
						selectVariant='multi'
					/>
				</div>
				<DialogFooter
					className={cn('justify-end', isMobile && 'mt-auto')}>
					<Button
						variant='outline'
						onClick={handleResetFilter}>
						{t('elements.buttons.reset')}
					</Button>
					<Button onClick={handleSubmit(handleSubmitFilterForm)}>
						{t('elements.buttons.apply')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default CalendarPageFilter;
